import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { useAuth } from '@/auth/AuthContext'
import { activate, activateAccount, resendActivation } from '@/api/auth'
import toast from 'react-hot-toast'

export default function ActivationPage() {
  const navigate = useNavigate()
  const { refreshUser } = useAuth()
  const [searchParams] = useSearchParams()

  const urlEmail = searchParams.get('email') || ''
  const urlCode = searchParams.get('code') || ''
  // setup=1 — account created for the user by an instance admin from the
  // waitlist: they must choose a password here instead of auto-activating.
  const urlSetup = searchParams.get('setup') === '1'

  const [email, setEmail] = useState(urlEmail)
  const [activationCode, setActivationCode] = useState(urlCode)
  const [needsPassword, setNeedsPassword] = useState(urlSetup)
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [autoActivating, setAutoActivating] = useState(false)
  const [resending, setResending] = useState(false)

  // Auto-activate if both email and code are in URL params (unless a password
  // still has to be chosen).
  useEffect(() => {
    if (urlEmail && urlCode && !urlSetup) {
      handleAutoActivate(urlEmail, urlCode)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleAutoActivate = async (activateEmail: string, code: string) => {
    setAutoActivating(true)
    try {
      const result = await activateAccount(activateEmail, code)
      // Admin-granted account reached without setup=1 — fall through to the
      // form so the user can set a password.
      if (result.needs_password) {
        setNeedsPassword(true)
        setAutoActivating(false)
        return
      }
      // The organization requires two-factor sign-in from the first sign-in:
      // the login page runs the setup.
      if (result.mfa_required || result.mfa_enrolment_required) {
        toast.success('Account activated — sign in to set up two-factor sign-in.')
        navigate('/login', { replace: true })
        return
      }
      toast.success(result.message || 'Account activated!')
      await refreshUser()
      navigate(result.redirect || '/workbench', { replace: true })
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Activation failed'
      toast.error(msg)
      setAutoActivating(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email || !activationCode) {
      toast.error('Please enter both email and activation code')
      return
    }
    if (needsPassword) {
      if (!password || !confirmPassword) {
        toast.error('Please choose a password')
        return
      }
      if (password !== confirmPassword) {
        toast.error('Passwords do not match')
        return
      }
      if (password.length < 8) {
        toast.error('Password must be at least 8 characters')
        return
      }
    }

    setLoading(true)
    try {
      const result = await activate({
        email,
        activation_code: activationCode,
        ...(needsPassword ? { password, confirm_password: confirmPassword } : {}),
      })
      if (result.mfa_required || result.mfa_enrolment_required) {
        toast.success('Account activated — sign in to set up two-factor sign-in.')
        navigate('/login', { replace: true })
        return
      }
      toast.success(result.message || 'Account activated!')
      await refreshUser()
      navigate(result.redirect || '/workbench', { replace: true })
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Activation failed'
      // Backend says this account still needs a password — reveal the fields.
      if (/password/i.test(msg) && !needsPassword) setNeedsPassword(true)
      toast.error(msg)
    } finally {
      setLoading(false)
    }
  }

  const handleResend = async () => {
    if (!email) {
      toast.error('Please enter your email first')
      return
    }
    setResending(true)
    try {
      await resendActivation(email)
      toast.success('Activation code resent! Check your email.')
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Failed to resend'
      toast.error(msg)
    } finally {
      setResending(false)
    }
  }

  if (autoActivating) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: 'var(--color-bg)',
        gap: '1.5rem',
      }}>
        <div className="spinner spinner-lg" />
        <p style={{ color: 'var(--color-text-secondary)', fontSize: '1rem' }}>
          Activating your account...
        </p>
      </div>
    )
  }

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      background: 'var(--color-bg)',
      padding: '2rem',
    }}>
      <div style={{
        maxWidth: 420,
        width: '100%',
        animation: 'slideUp 0.3s ease-out',
      }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <span style={{
            fontFamily: 'var(--font-heading)',
            fontSize: '1.8125rem',
            fontWeight: 700,
            background: 'linear-gradient(90deg, #0061D0, #009974)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}>
            twasta.ai
          </span>
        </div>

        <div style={{
          background: 'var(--color-bg-secondary)',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-lg)',
          padding: '2rem',
        }}>
          <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
            <span className="material-icons" style={{
              fontSize: 41,
              color: 'var(--color-accent)',
              marginBottom: '0.75rem',
              display: 'block',
            }}>
              verified_user
            </span>
            <h2 style={{ fontSize: '1.3125rem', fontWeight: 600, marginBottom: '0.375rem' }}>
              {needsPassword ? 'Set Up Your Account' : 'Activate Your Account'}
            </h2>
            <p style={{
              color: 'var(--color-text-secondary)',
              fontSize: '0.9375rem',
            }}>
              {needsPassword
                ? 'Your access has been approved. Choose a password to finish setting up your account.'
                : 'Enter the activation code from your email.'}
            </p>
          </div>

          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
            <div>
              <label htmlFor="act-email">Email Address</label>
              <input
                id="act-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                required
              />
            </div>

            <div>
              <label htmlFor="act-code">Activation Code</label>
              <input
                id="act-code"
                type="text"
                value={activationCode}
                onChange={(e) => setActivationCode(e.target.value)}
                placeholder="Enter 6-digit code"
                style={{
                  fontFamily: 'monospace',
                  fontSize: '1.1875rem',
                  letterSpacing: '0.2em',
                  textAlign: 'center',
                }}
                required
                autoFocus={!!urlEmail && !needsPassword}
              />
            </div>

            {needsPassword && (
              <>
                <div>
                  <label htmlFor="act-password">Choose a Password</label>
                  <input
                    id="act-password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="At least 8 characters"
                    autoComplete="new-password"
                    minLength={8}
                    required
                    autoFocus
                  />
                </div>
                <div>
                  <label htmlFor="act-password-confirm">Confirm Password</label>
                  <input
                    id="act-password-confirm"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Re-enter your password"
                    autoComplete="new-password"
                    minLength={8}
                    required
                  />
                </div>
              </>
            )}

            <button
              type="submit"
              className="btn btn-primary btn-lg w-full"
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="spinner" style={{ width: 18, height: 18, borderWidth: 2 }} />
                  Activating...
                </>
              ) : (
                <>
                  <span className="material-icons" style={{ fontSize: 19 }}>check_circle</span>
                  {needsPassword ? 'Activate & Set Password' : 'Activate Account'}
                </>
              )}
            </button>
          </form>

          <div style={{
            textAlign: 'center',
            marginTop: '1.25rem',
            paddingTop: '1rem',
            borderTop: '1px solid var(--color-border)',
          }}>
            <button
              onClick={handleResend}
              disabled={resending}
              style={{
                background: 'none',
                color: 'var(--color-accent)',
                fontSize: '0.9375rem',
                cursor: 'pointer',
              }}
            >
              {resending ? 'Sending...' : "Didn't receive the code? Resend"}
            </button>
          </div>
        </div>

        <div style={{
          textAlign: 'center',
          marginTop: '1.25rem',
          fontSize: '0.9375rem',
          color: 'var(--color-text-secondary)',
        }}>
          <Link to="/">Back to Login</Link>
        </div>
      </div>
    </div>
  )
}
